import { AUDITOR_PRIVILEGES } from '../constants/auditorPrivileges';

/**
 * @typedef {Object} AuditorAccess
 * @property {boolean} canViewAuditorOutput Findings already made stay readable without the add-on.
 * @property {boolean} canViewAuditorBehavior Whether the controls that shape the auditor render.
 * @property {boolean} canRunCheckoutAudit Whether a note may be audited before signing.
 * @property {boolean} canManageAuditorBehavior Whether those controls act rather than sit inert.
 * @property {boolean} canOperateAuditor Whether the auditor acts for this user at all — the
 *   organization has it, or staff are operating inside one that has not.
 * @property {boolean} hasAuditorEntitlement Whether the organization may use the auditor. States the
 *   fact; each surface decides for itself whether its absence greys a control or hides it.
 * @property {boolean} isStaffBypass Operating it in an organization that has not got it. The backend
 *   draws the same distinction as `isInternalBypass` and logs it.
 */

/**
 * @param {Object} deps
 * @param {(privilege: string) => boolean} deps.hasPrivilege
 * @param {boolean} [deps.isEntitled] Whether the organization may use the auditor — it holds the
 *   add-on, or enforcement has not started. Never folds in the staff bypass; that is `isStaff`.
 * @param {boolean} [deps.isStaff] Whether the user is Hi Rasmus staff.
 * @return {AuditorAccess}
 */
// eslint-disable-next-line import/prefer-default-export
export function resolveAuditorAccess({ hasPrivilege, isEntitled = false, isStaff = false }) {
  const managesBehavior = isStaff || hasPrivilege(AUDITOR_PRIVILEGES.MANAGE_BEHAVIOR);
  // Shaping the auditor is the supervisory half of reading it, so it carries reading with it.
  const readsOutput = managesBehavior || hasPrivilege(AUDITOR_PRIVILEGES.USE_OUTPUT);
  // Staff operate it inside an organization that has not got it, which is what CS demos on.
  const mayOperate = isEntitled || isStaff;

  return {
    canOperateAuditor: mayOperate,
    canViewAuditorOutput: readsOutput,
    canViewAuditorBehavior: managesBehavior,
    canRunCheckoutAudit: mayOperate && readsOutput,
    canManageAuditorBehavior: mayOperate && managesBehavior,
    hasAuditorEntitlement: isEntitled,
    isStaffBypass: !isEntitled && isStaff,
  };
} // resolveAuditorAccess()
